/**
 * The forest: thousands of trees drawn as chunked instanced meshes.
 *
 * Two things make this affordable. Instancing collapses each variant within a
 * chunk into a single draw call, and chunking lets us hide everything the fog
 * would have swallowed anyway. The result is that visible geometry stays
 * roughly constant no matter how large the world gets.
 */

import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { ATMOSPHERE, TREES, type World, type TreeInstance } from '@static-pines/shared';

/** Trees are modelled at this height and scaled per instance. */
const NOMINAL_HEIGHT = 12;
const CHUNK_SIZE = 40;

// Albedo, not final appearance. These read as near-black under the night
// lighting; picking them "already dark" is what made the first build render a
// literally black screen.
const BARK = new THREE.Color(0x6b5645);
const BARK_DEAD = new THREE.Color(0x827567);
const NEEDLE = new THREE.Color(0x33502a);
const NEEDLE_DARK = new THREE.Color(0x263e20);
const LEAF = new THREE.Color(0x415f2d);

/**
 * Tint a part and normalise it for merging.
 *
 * The de-indexing is not optional: three's primitives are inconsistent about
 * indexing (cylinders and cones are indexed, icosahedra are not), and
 * mergeGeometries rejects a mixed set outright. Converting everything to
 * non-indexed up front is the cheapest way to guarantee they combine. These
 * are tiny geometries uploaded once, so the extra vertices cost nothing.
 */
function paint(source: THREE.BufferGeometry, color: THREE.Color): THREE.BufferGeometry {
  const geometry = source.index ? source.toNonIndexed() : source;

  const count = geometry.attributes.position!.count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // Per-vertex jitter stops large instanced batches reading as flat blocks.
    const j = 0.82 + ((i * 0.6180339887) % 1) * 0.36;
    colors[i * 3] = color.r * j;
    colors[i * 3 + 1] = color.g * j;
    colors[i * 3 + 2] = color.b * j;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geometry;
}

/** Merge with a clear failure, rather than a null that explodes later. */
function merge(parts: THREE.BufferGeometry[], label: string): THREE.BufferGeometry {
  const merged = BufferGeometryUtils.mergeGeometries(parts, false);
  if (!merged) throw new Error(`Failed to merge geometry for tree variant "${label}"`);
  merged.computeVertexNormals();
  return merged;
}

/** A tall conifer — the workhorse of the forest. */
function buildConifer(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const trunk = new THREE.CylinderGeometry(0.16, 0.34, NOMINAL_HEIGHT, 6, 1);
  trunk.translate(0, NOMINAL_HEIGHT / 2, 0);
  parts.push(paint(trunk, BARK));

  // Three stacked skirts, narrowing toward the crown.
  const tiers = [
    { y: 0.38, radius: 2.5, height: 4.2, color: NEEDLE_DARK },
    { y: 0.58, radius: 2.0, height: 3.8, color: NEEDLE },
    { y: 0.78, radius: 1.4, height: 3.2, color: NEEDLE },
  ];
  for (const tier of tiers) {
    const cone = new THREE.ConeGeometry(tier.radius, tier.height, 7, 1);
    cone.translate(0, NOMINAL_HEIGHT * tier.y + tier.height / 2, 0);
    parts.push(paint(cone, tier.color));
  }

  return merge(parts, 'conifer');
}

/** A dead, branchless snag. Reads as a silhouette in the fog. */
function buildSnag(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const trunk = new THREE.CylinderGeometry(0.1, 0.3, NOMINAL_HEIGHT * 0.9, 5, 1);
  trunk.translate(0, (NOMINAL_HEIGHT * 0.9) / 2, 0);
  parts.push(paint(trunk, BARK_DEAD));

  // A few broken limbs at angles.
  const limbs = [
    { y: 0.55, angle: 0.4, tilt: 0.9 },
    { y: 0.7, angle: 2.5, tilt: 1.1 },
    { y: 0.45, angle: 4.4, tilt: 0.75 },
  ];
  for (const limb of limbs) {
    const branch = new THREE.CylinderGeometry(0.05, 0.11, 2.6, 4, 1);
    branch.translate(0, 1.3, 0);
    branch.rotateZ(limb.tilt);
    branch.rotateY(limb.angle);
    branch.translate(0, NOMINAL_HEIGHT * limb.y, 0);
    parts.push(paint(branch, BARK_DEAD));
  }

  return merge(parts, 'snag');
}

/** A shorter broadleaf with a lumpy canopy. */
function buildBroadleaf(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const trunk = new THREE.CylinderGeometry(0.2, 0.36, NOMINAL_HEIGHT * 0.62, 6, 1);
  trunk.translate(0, (NOMINAL_HEIGHT * 0.62) / 2, 0);
  parts.push(paint(trunk, BARK));

  const blobs = [
    { x: 0, y: 0.72, z: 0, r: 2.6 },
    { x: 1.3, y: 0.63, z: 0.6, r: 1.8 },
    { x: -1.1, y: 0.66, z: -0.9, r: 1.9 },
  ];
  for (const blob of blobs) {
    const canopy = new THREE.IcosahedronGeometry(blob.r, 0);
    canopy.translate(blob.x, NOMINAL_HEIGHT * blob.y, blob.z);
    parts.push(paint(canopy, LEAF));
  }

  return merge(parts, 'broadleaf');
}

interface Chunk {
  cx: number;
  cz: number;
  centerX: number;
  centerZ: number;
  meshes: THREE.InstancedMesh[];
}

/** How far a footfall reaches into the trees. */
const SHAKE_RADIUS = 46;

export class Forest {
  readonly group = new THREE.Group();

  private readonly chunks: Chunk[] = [];
  private readonly cullDistance: number;

  /**
   * Shared shake uniforms.
   *
   * Displacing thousands of instanced trees on the CPU would mean rewriting
   * every instance matrix each frame. Doing it in the vertex shader costs
   * nothing: the whole forest reacts to a stomp from three uniforms.
   */
  private readonly shakeOrigin = { value: new THREE.Vector3() };
  private readonly shakeAmount = { value: 0 };
  private readonly shakeTime = { value: 0 };

  constructor(world: World) {
    this.group.name = 'forest';
    this.cullDistance = ATMOSPHERE.DRAW_DISTANCE + CHUNK_SIZE;

    const geometries = [buildConifer(), buildSnag(), buildBroadleaf()];
    const material = new THREE.MeshLambertMaterial({ vertexColors: true, fog: true });

    material.onBeforeCompile = (shader) => {
      shader.uniforms.uShakeOrigin = this.shakeOrigin;
      shader.uniforms.uShakeAmount = this.shakeAmount;
      shader.uniforms.uShakeTime = this.shakeTime;

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform vec3 uShakeOrigin;
           uniform float uShakeAmount;
           uniform float uShakeTime;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           if (uShakeAmount > 0.001) {
             // Instance matrix places the trunk; we need its world footing to
             // know how far it stands from the impact.
             vec3 treeBase = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
             float dist = distance(treeBase.xz, uShakeOrigin.xz);
             float falloff = 1.0 - smoothstep(0.0, ${SHAKE_RADIUS.toFixed(1)}, dist);

             if (falloff > 0.0) {
               // Canopies swing, the base of the trunk does not.
               float lever = clamp(transformed.y / 11.0, 0.0, 1.0);
               lever *= lever;

               // Offset by distance so the disturbance travels outward
               // instead of the whole stand twitching in unison.
               float phase = uShakeTime * 13.0 - dist * 0.28;
               float sway = sin(phase) * uShakeAmount * falloff * lever;

               transformed.x += sway;
               transformed.z += sway * 0.7;
             }
           }`,
        );
    };

    // Bucket every tree by chunk and variant before touching the GPU.
    const buckets = new Map<string, TreeInstance[][]>();

    for (const tree of world.trees) {
      const cx = Math.floor(tree.x / CHUNK_SIZE);
      const cz = Math.floor(tree.z / CHUNK_SIZE);
      const key = `${cx},${cz}`;

      let byVariant = buckets.get(key);
      if (!byVariant) {
        byVariant = Array.from({ length: TREES.VARIANTS }, () => [] as TreeInstance[]);
        buckets.set(key, byVariant);
      }
      byVariant[tree.variant % TREES.VARIANTS]!.push(tree);
    }

    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const euler = new THREE.Euler();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();

    for (const [key, byVariant] of buckets) {
      const [cxStr, czStr] = key.split(',');
      const cx = Number(cxStr);
      const cz = Number(czStr);

      const chunk: Chunk = {
        cx,
        cz,
        centerX: (cx + 0.5) * CHUNK_SIZE,
        centerZ: (cz + 0.5) * CHUNK_SIZE,
        meshes: [],
      };

      for (let variant = 0; variant < TREES.VARIANTS; variant++) {
        const trees = byVariant[variant]!;
        if (trees.length === 0) continue;

        const mesh = new THREE.InstancedMesh(geometries[variant]!, material, trees.length);
        mesh.castShadow = true;
        mesh.receiveShadow = false;
        // Chunks are culled manually against the fog radius.
        mesh.frustumCulled = true;

        for (let i = 0; i < trees.length; i++) {
          const tree = trees[i]!;
          const uniformScale = tree.height / NOMINAL_HEIGHT;

          position.set(tree.x, tree.y, tree.z);
          euler.set(tree.tilt, tree.rotation, tree.tilt * 0.7);
          quaternion.setFromEuler(euler);
          scale.setScalar(uniformScale);

          matrix.compose(position, quaternion, scale);
          mesh.setMatrixAt(i, matrix);
        }

        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
        chunk.meshes.push(mesh);
        this.group.add(mesh);
      }

      this.chunks.push(chunk);
    }
  }

  /** Kick the trees near a footfall. `strength` is roughly metres of sway. */
  shake(x: number, y: number, z: number, strength: number): void {
    this.shakeOrigin.value.set(x, y, z);
    // Take the stronger of the two rather than summing, so a burst of
    // footsteps does not compound into the forest thrashing.
    this.shakeAmount.value = Math.max(this.shakeAmount.value, strength);
    this.shakeTime.value = 0;
  }

  /** Decay the current shake. Separate from update() so it is frame-rate safe. */
  tickShake(dt: number): void {
    if (this.shakeAmount.value <= 0.001) {
      this.shakeAmount.value = 0;
      return;
    }
    this.shakeTime.value += dt;
    // Roughly a second of ring-out.
    this.shakeAmount.value *= Math.exp(-4.5 * dt);
  }

  /**
   * Hide chunks the fog would have hidden anyway. Called every frame; cheap
   * enough that there is no reason to throttle it.
   */
  update(viewerX: number, viewerZ: number): void {
    const limitSq = this.cullDistance * this.cullDistance;

    for (const chunk of this.chunks) {
      const dx = chunk.centerX - viewerX;
      const dz = chunk.centerZ - viewerZ;
      const visible = dx * dx + dz * dz < limitSq;

      for (const mesh of chunk.meshes) {
        mesh.visible = visible;
      }
    }
  }

  get chunkCount(): number {
    return this.chunks.length;
  }
}
